import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  BookOpen,
  DollarSign,
  Image,
  Video,
  ListChecks,
  Sparkles,
  Plus,
  X,
  ChevronRight,
  Save,
  Eye,
  Radio,
  Users,
  Calendar,
  Zap,
  Info,
  CheckCircle2
} from 'lucide-react';
import { coursesApi } from '../../lib/api';
import MediaUploader from '../../components/MediaUploader';
import toast from 'react-hot-toast';

interface Category {
  id: string;
  name: string;
  slug: string;
}

type CourseType = 'RECORDED' | 'LIVE';

interface CourseFormData {
  title: string;
  description: string;
  shortDescription: string;
  thumbnailUrl: string;
  previewVideoUrl: string;
  price: number;
  currency: string;
  categoryId: string;
  level: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
  language: string;
  requirements: string[];
  whatYouWillLearn: string[];
  courseType: CourseType;
  hasLiveMeetingAddon: boolean;
  liveMeetingAddonFee: number;
  totalSessions: number;
}

const LEVELS = [
  { value: 'BEGINNER', label: 'Beginner', description: 'No prior knowledge required' },
  { value: 'INTERMEDIATE', label: 'Intermediate', description: 'Some experience helpful' },
  { value: 'ADVANCED', label: 'Advanced', description: 'For experienced learners' },
];

const LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Portuguese', 
  'Italian', 'Russian', 'Chinese', 'Japanese', 'Korean',
  'Arabic', 'Hindi', 'Dutch', 'Polish', 'Turkish'
];

const LIVE_MEETING_MIN_FEE = 9.99;

export default function CreateCoursePage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for course type selection
  const [newRequirement, setNewRequirement] = useState('');
  const [newLearningPoint, setNewLearningPoint] = useState('');
  
  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    description: '',
    shortDescription: '',
    thumbnailUrl: '',
    previewVideoUrl: '',
    price: 0,
    currency: 'USD',
    categoryId: '',
    level: 'BEGINNER',
    language: 'English',
    requirements: [],
    whatYouWillLearn: [],
    courseType: 'RECORDED',
    hasLiveMeetingAddon: false,
    liveMeetingAddonFee: LIVE_MEETING_MIN_FEE,
    totalSessions: 0,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CourseFormData, string>>>({});

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await coursesApi.getCategories();
        if (res.data.success) {
          setCategories(res.data.data as Category[]);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof CourseFormData, string>> = {};

    // Step 1: Basic Info validation
    if (step === 1) {
      if (!formData.title || formData.title.length < 5) {
        newErrors.title = 'Title must be at least 5 characters';
      }
      if (!formData.description || formData.description.length < 50) {
        newErrors.description = 'Description must be at least 50 characters';
      }
      if (!formData.categoryId) {
        newErrors.categoryId = 'Please select a category';
      }
      if (formData.courseType === 'LIVE' && formData.totalSessions < 1) {
        newErrors.totalSessions = 'Live courses must have at least 1 session planned';
      }
    }

    // Step 4: Pricing validation (now the last step)
    if (step === 4) {
      if (formData.price < 0 || formData.price > 9999.99) {
        newErrors.price = 'Price must be between 0 and 9999.99';
      }
      if (formData.hasLiveMeetingAddon && formData.liveMeetingAddonFee < LIVE_MEETING_MIN_FEE) {
        newErrors.liveMeetingAddonFee = `Live meeting fee must be at least $${LIVE_MEETING_MIN_FEE}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 0 || validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleCourseTypeSelect = (type: CourseType) => {
    setFormData(prev => ({
      ...prev,
      courseType: type,
      hasLiveMeetingAddon: false, // Reset addon when type changes
      liveMeetingAddonFee: LIVE_MEETING_MIN_FEE,
      totalSessions: type === 'LIVE' ? 1 : 0,
    }));
  };

  const handleAddRequirement = () => {
    if (newRequirement.trim() && formData.requirements.length < 20) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const handleRemoveRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleAddLearningPoint = () => {
    if (newLearningPoint.trim() && formData.whatYouWillLearn.length < 20) {
      setFormData(prev => ({
        ...prev,
        whatYouWillLearn: [...prev.whatYouWillLearn, newLearningPoint.trim()]
      }));
      setNewLearningPoint('');
    }
  };

  const handleRemoveLearningPoint = (index: number) => {
    setFormData(prev => ({
      ...prev,
      whatYouWillLearn: prev.whatYouWillLearn.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (saveAsDraft: boolean = true) => {
    if (!validateStep(1) || !validateStep(4)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const courseData = {
        ...formData,
        price: Number(formData.price),
        liveMeetingAddonFee: formData.hasLiveMeetingAddon ? Number(formData.liveMeetingAddonFee) : 0,
        shortDescription: formData.shortDescription || undefined,
        thumbnailUrl: formData.thumbnailUrl || undefined,
        previewVideoUrl: formData.previewVideoUrl || undefined,
      };

      const res = await coursesApi.create(courseData);
      
      if (res.data.success) {
        toast.success(saveAsDraft ? 'Course saved as draft!' : 'Course created successfully!');
        navigate('/professor/courses');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; details?: Array<{ message: string }> } } };
      console.error('Failed to create course:', error);
      toast.error(err.response?.data?.error || 'Failed to create course');
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalPrice = () => {
    let total = formData.price;
    if (formData.hasLiveMeetingAddon) {
      total += formData.liveMeetingAddonFee;
    }
    return total;
  };

  const steps = [
    { number: 0, title: 'Course Type', icon: Radio },
    { number: 1, title: 'Basic Info', icon: BookOpen },
    { number: 2, title: 'Media', icon: Image },
    { number: 3, title: 'Details', icon: ListChecks },
    { number: 4, title: 'Pricing', icon: DollarSign },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/professor/courses')}
          className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Courses</span>
        </button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-coral/20 to-red-500/20 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-coral" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">Create New Course</h1>
            <p className="text-white/50">Fill in the details to create your course</p>
          </div>
        </div>
      </motion.div>

      {/* Progress Steps */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8 overflow-x-auto"
      >
        <div className="flex items-center justify-between min-w-max">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <button
                onClick={() => currentStep > step.number && setCurrentStep(step.number)}
                disabled={currentStep < step.number}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  currentStep === step.number
                    ? 'bg-coral/20 border border-coral/30'
                    : currentStep > step.number
                    ? 'bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10'
                    : 'bg-white/[0.02] border border-white/5 cursor-not-allowed'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  currentStep === step.number
                    ? 'bg-coral text-white'
                    : currentStep > step.number
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-white/10 text-white/30'
                }`}>
                  {currentStep > step.number ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                </div>
                <span className={`font-medium hidden sm:block ${
                  currentStep >= step.number ? 'text-white' : 'text-white/30'
                }`}>
                  {step.title}
                </span>
              </button>
              {index < steps.length - 1 && (
                <ChevronRight className="w-5 h-5 text-white/20 mx-2 hidden md:block" />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Form Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl p-8"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          {/* Step 0: Course Type Selection */}
          {currentStep === 0 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">What type of course are you creating?</h2>
                <p className="text-white/50">Choose the format that best fits your teaching style</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Recorded Course Option */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleCourseTypeSelect('RECORDED')}
                  className={`relative p-6 rounded-2xl text-left transition-all duration-300 ${
                    formData.courseType === 'RECORDED'
                      ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border-2 border-blue-500/50'
                      : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
                  }`}
                >
                  {formData.courseType === 'RECORDED' && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle2 className="w-6 h-6 text-blue-400" />
                    </div>
                  )}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                    <Video className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Video-Based Course</h3>
                  <p className="text-white/50 mb-4">
                    Pre-recorded lessons with videos, documents, and quizzes. Students learn at their own pace, like Udemy.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Upload video lessons & documents
                    </li>
                    <li className="flex items-center gap-2 text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Self-paced learning for students
                    </li>
                    <li className="flex items-center gap-2 text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Optional live Q&A session (add-on)
                    </li>
                  </ul>
                </motion.button>

                {/* Live Course Option */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleCourseTypeSelect('LIVE')}
                  className={`relative p-6 rounded-2xl text-left transition-all duration-300 ${
                    formData.courseType === 'LIVE'
                      ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/10 border-2 border-purple-500/50'
                      : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
                  }`}
                >
                  {formData.courseType === 'LIVE' && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle2 className="w-6 h-6 text-purple-400" />
                    </div>
                  )}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                    <Radio className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Live Session Course</h3>
                  <p className="text-white/50 mb-4">
                    Real-time interactive sessions via video meetings. Teach live with direct student engagement.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Schedule live teaching sessions
                    </li>
                    <li className="flex items-center gap-2 text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Real-time interaction with students
                    </li>
                    <li className="flex items-center gap-2 text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Recordings available after sessions
                    </li>
                  </ul>
                </motion.button>
              </div>

              {/* Info notice */}
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-200 text-sm font-medium">Important Note</p>
                  <p className="text-amber-200/70 text-sm">
                    You cannot mix course types. A course is either entirely video-based or entirely live sessions. 
                    However, video-based courses can include an optional live Q&A session at the end for an additional fee.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Course Type Badge */}
              <div className="flex items-center gap-2 mb-6">
                <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  formData.courseType === 'LIVE' 
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                }`}>
                  {formData.courseType === 'LIVE' ? (
                    <span className="flex items-center gap-2">
                      <Radio className="w-4 h-4" />
                      Live Session Course
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Video-Based Course
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="label">Course Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Complete Web Development Bootcamp"
                  className={`input ${errors.title ? 'input-error' : ''}`}
                />
                {errors.title && <p className="text-coral text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="label">Short Description</label>
                <input
                  type="text"
                  value={formData.shortDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                  placeholder="A brief summary of your course (max 500 characters)"
                  maxLength={500}
                  className="input"
                />
                <p className="text-white/30 text-xs mt-1">{formData.shortDescription.length}/500 characters</p>
              </div>

              <div>
                <label className="label">Full Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Provide a detailed description of what students will learn..."
                  rows={6}
                  className={`input resize-none ${errors.description ? 'input-error' : ''}`}
                />
                {errors.description && <p className="text-coral text-sm mt-1">{errors.description}</p>}
                <p className="text-white/30 text-xs mt-1">{formData.description.length} characters (min 50)</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Category *</label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                    className={`input ${errors.categoryId ? 'input-error' : ''}`}
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.categoryId && <p className="text-coral text-sm mt-1">{errors.categoryId}</p>}
                </div>

                <div>
                  <label className="label">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value as CourseFormData['level'] }))}
                    className="input"
                  >
                    {LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Language</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                  className="input"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>

              {/* Live course specific - number of sessions */}
              {formData.courseType === 'LIVE' && (
                <div>
                  <label className="label">Planned Number of Sessions *</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.totalSessions || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        const num = parseInt(value) || 0;
                        setFormData(prev => ({ ...prev, totalSessions: Math.min(num, 100) }));
                      }}
                      placeholder="How many live sessions?"
                      className={`input pl-12 ${errors.totalSessions ? 'input-error' : ''}`}
                    />
                  </div>
                  {errors.totalSessions && <p className="text-coral text-sm mt-1">{errors.totalSessions}</p>}
                  <p className="text-white/30 text-xs mt-2">You'll schedule specific dates/times after creating the course</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Media */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="label">Course Thumbnail</label>
                <MediaUploader
                  type="thumbnail"
                  value={formData.thumbnailUrl}
                  onChange={(url) => setFormData(prev => ({ ...prev, thumbnailUrl: url }))}
                  placeholder="Upload a thumbnail or paste a URL"
                />
              </div>

              {/* Preview Video - Only for recorded courses */}
              {formData.courseType === 'RECORDED' && (
                <div>
                  <label className="label">Preview Video</label>
                  <MediaUploader
                    type="preview-video"
                    value={formData.previewVideoUrl}
                    onChange={(url) => setFormData(prev => ({ ...prev, previewVideoUrl: url }))}
                    placeholder="Upload a preview video or paste a URL (YouTube, Vimeo, etc.)"
                  />
                  <p className="text-white/30 text-xs mt-2">A 2-5 minute preview helps students understand your teaching style</p>
                </div>
              )}

              <div className={`p-6 rounded-xl border ${
                formData.courseType === 'LIVE' 
                  ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20'
                  : 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    formData.courseType === 'LIVE' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                  }`}>
                    <Eye className={`w-5 h-5 ${formData.courseType === 'LIVE' ? 'text-purple-400' : 'text-blue-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">
                      {formData.courseType === 'LIVE' ? 'Live Course Media Tips' : 'Media Tips'}
                    </h4>
                    <ul className="text-sm text-white/50 space-y-1">
                      <li>• Use a high-quality thumbnail that represents your course</li>
                      {formData.courseType === 'RECORDED' ? (
                        <>
                          <li>• Preview videos should showcase your teaching style</li>
                          <li>• Include clips from actual course content if possible</li>
                        </>
                      ) : (
                        <>
                          <li>• For live courses, the thumbnail is especially important</li>
                          <li>• Consider including your photo or session schedule visual</li>
                          <li>• Students will join live - no preview video needed</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {currentStep === 3 && (
            <div className="space-y-8">
              {/* Requirements */}
              <div>
                <label className="label">Course Requirements</label>
                <p className="text-white/40 text-sm mb-4">What should students know or have before taking this course?</p>
                
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRequirement()}
                    placeholder="e.g., Basic understanding of HTML"
                    className="input flex-1"
                  />
                  <button
                    onClick={handleAddRequirement}
                    disabled={!newRequirement.trim() || formData.requirements.length >= 20}
                    className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  {formData.requirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 group">
                      <span className="text-white/70 flex-1">{req}</span>
                      <button
                        onClick={() => handleRemoveRequirement(index)}
                        className="text-white/30 hover:text-coral transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {formData.requirements.length === 0 && (
                  <p className="text-white/30 text-sm italic">No requirements added yet</p>
                )}
              </div>

              {/* What You'll Learn */}
              <div>
                <label className="label">What Students Will Learn</label>
                <p className="text-white/40 text-sm mb-4">List the key outcomes and skills students will gain</p>
                
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={newLearningPoint}
                    onChange={(e) => setNewLearningPoint(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddLearningPoint()}
                    placeholder="e.g., Build responsive websites from scratch"
                    className="input flex-1"
                  />
                  <button
                    onClick={handleAddLearningPoint}
                    disabled={!newLearningPoint.trim() || formData.whatYouWillLearn.length >= 20}
                    className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  {formData.whatYouWillLearn.map((point, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 group">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      </div>
                      <span className="text-white/70 flex-1">{point}</span>
                      <button
                        onClick={() => handleRemoveLearningPoint(index)}
                        className="text-white/30 hover:text-coral transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {formData.whatYouWillLearn.length === 0 && (
                  <p className="text-white/30 text-sm italic">No learning outcomes added yet</p>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Pricing (Final step before publish) */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4">Course Pricing</h3>
                <p className="text-white/50 mb-6">Set a competitive price for your course. You can always update it later.</p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Base Price (USD) *</label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.price || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Only allow one decimal point
                          const parts = value.split('.');
                          const sanitized = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('').slice(0, 2) : '');
                          const num = parseFloat(sanitized) || 0;
                          setFormData(prev => ({ ...prev, price: Math.min(num, 9999.99) }));
                        }}
                        placeholder="0.00"
                        className={`input pl-12 ${errors.price ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.price && <p className="text-coral text-sm mt-1">{errors.price}</p>}
                    <p className="text-white/30 text-xs mt-2">Set to 0 for a free course</p>
                  </div>

                  <div>
                    <label className="label">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="input"
                    >
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Live Meeting Addon (only for RECORDED courses) */}
              {formData.courseType === 'RECORDED' && (
                <div className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/5 border border-purple-500/20">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">Live Q&A Session Add-on</h4>
                        <p className="text-sm text-white/50">Offer a live meeting at the end of the course</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        hasLiveMeetingAddon: !prev.hasLiveMeetingAddon 
                      }))}
                      className={`relative w-14 h-8 rounded-full transition-colors ${
                        formData.hasLiveMeetingAddon ? 'bg-purple-500' : 'bg-white/20'
                      }`}
                    >
                      <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${
                        formData.hasLiveMeetingAddon ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  {formData.hasLiveMeetingAddon && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-purple-500/20"
                    >
                      <div className="p-4 rounded-lg bg-white/5 mb-4">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-white/60">
                            Live meetings require server resources and incur costs. The additional fee helps cover 
                            hosting, bandwidth, and recording storage. Minimum fee: ${LIVE_MEETING_MIN_FEE}
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="label">Additional Live Meeting Fee (USD) *</label>
                        <div className="relative">
                          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formData.liveMeetingAddonFee || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Only allow one decimal point
                              const parts = value.split('.');
                              const sanitized = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('').slice(0, 2) : '');
                              const num = parseFloat(sanitized) || 0;
                              setFormData(prev => ({ 
                                ...prev, 
                                liveMeetingAddonFee: Math.min(num, 999.99)
                              }));
                            }}
                            placeholder={LIVE_MEETING_MIN_FEE.toString()}
                            className={`input pl-12 border-purple-500/30 focus:border-purple-500/50 ${
                              errors.liveMeetingAddonFee ? 'input-error' : ''
                            }`}
                          />
                        </div>
                        {errors.liveMeetingAddonFee && (
                          <p className="text-coral text-sm mt-1">{errors.liveMeetingAddonFee}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Earnings Breakdown */}
              {(formData.price > 0 || formData.hasLiveMeetingAddon) && (
                <div className="p-6 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20">
                  <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-emerald-400" />
                    Earnings Breakdown (per enrollment)
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Base Course Price</span>
                      <span className="text-white font-medium">${formData.price.toFixed(2)}</span>
                    </div>
                    {formData.hasLiveMeetingAddon && (
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Live Q&A Session Add-on</span>
                        <span className="text-purple-400 font-medium">+${formData.liveMeetingAddonFee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="h-px bg-white/10" />
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Total Price</span>
                      <span className="text-white font-medium">${getTotalPrice().toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Platform Fee (20%)</span>
                      <span className="text-coral font-medium">-${(getTotalPrice() * 0.20).toFixed(2)}</span>
                    </div>
                    <div className="h-px bg-white/10 my-2" />
                    <div className="flex items-center justify-between">
                      <span className="text-white font-semibold">Your Earnings</span>
                      <span className="text-emerald-400 font-bold text-lg">${(getTotalPrice() * 0.80).toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-white/40 text-xs mt-4">
                    * Platform fee covers payment processing, hosting, and platform maintenance
                  </p>
                </div>
              )}

              <div className="p-6 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Pricing Tips</h4>
                    <ul className="text-sm text-white/50 space-y-1">
                      <li>• Research similar courses to set competitive pricing</li>
                      <li>• Consider offering an introductory price for new courses</li>
                      <li>• Higher-priced courses often attract more serious students</li>
                      {formData.courseType === 'LIVE' && (
                        <li>• Live courses can typically command 20-50% higher prices</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Course Summary - Final Review */}
              <div className="p-6 rounded-xl bg-gradient-to-br from-coral/10 to-purple-500/10 border border-coral/20">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-coral" />
                  Course Summary
                </h4>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <span className="text-white/50">Course Type</span>
                    <span className="text-white font-medium">
                      {formData.courseType === 'LIVE' ? 'Live Sessions' : 'Video-Based'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <span className="text-white/50">Category</span>
                    <span className="text-white font-medium">
                      {categories.find(c => c.id === formData.categoryId)?.name || 'Not set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <span className="text-white/50">Level</span>
                    <span className="text-white font-medium">{formData.level}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <span className="text-white/50">Price</span>
                    <span className="text-emerald-400 font-medium">${getTotalPrice().toFixed(2)}</span>
                  </div>
                  {formData.hasLiveMeetingAddon && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-white/50">Live Q&A</span>
                      <span className="text-purple-400 font-medium">Included (+${formData.liveMeetingAddonFee.toFixed(2)})</span>
                    </div>
                  )}
                  {formData.courseType === 'LIVE' && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-white/50">Sessions</span>
                      <span className="text-white font-medium">{formData.totalSessions}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <span className="text-white/50">Requirements</span>
                    <span className="text-white font-medium">{formData.requirements.length} added</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <span className="text-white/50">Learning Outcomes</span>
                    <span className="text-white font-medium">{formData.whatYouWillLearn.length} added</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="px-6 py-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex items-center gap-3">
              {currentStep === 4 ? (
                <>
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={isLoading}
                    className="px-6 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    Save as Draft
                  </button>
                  <button
                    onClick={() => handleSubmit(false)}
                    disabled={isLoading}
                    className="btn-primary flex items-center gap-2"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Create Course
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleNext}
                  className="btn-primary flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
